import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  FormGroup,
  Link,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { CoverageFacets, CoverageLevel, JobFacets } from '../../types';
import {
  COVERAGE_LABELS,
  COVERAGE_LEVELS,
  FacetGroup,
  FilterOption,
  JobBoardState,
  POSTED_WITHIN_OPTIONS,
  agencyCaption,
  countSelected,
  facetRows,
  mergeFacetOptions,
  normalizeAmount,
  normalizeText,
  postedWithinLabel,
} from '../../utils/jobBoard';
import DebouncedTextField from './DebouncedTextField';

export type BoardChange = (patch: Partial<JobBoardState>, options?: { replace?: boolean }) => void;

interface Props {
  state: JobBoardState;
  /** null until the first counts arrive. */
  facets: JobFacets | null;
  facetsFailed: boolean;
  onRetryFacets: () => void;
  signedIn: boolean;
  /** Staff may also filter by hiring system. */
  staff?: boolean;
  onChange: BoardChange;
}

const formatCount = (count: number | null): string => (count === null ? '' : count.toLocaleString('en-US'));

const optionRowSx = {
  mx: 0,
  alignItems: 'flex-start',
  '& .MuiFormControlLabel-label': { flex: 1, minWidth: 0, fontSize: '0.875rem', py: '5px' },
} as const;

const OptionLabel: React.FC<{ label: string; count: number | null }> = ({ label, count }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
    <Box component="span" sx={{ overflowWrap: 'anywhere' }}>
      {label}
    </Box>
    <Box component="span" sx={{ color: 'text.secondary', fontSize: '0.75rem', fontVariantNumeric: 'tabular-nums', pt: '2px' }}>
      {formatCount(count)}
    </Box>
  </Box>
);

// ---------------------------------------------------------------------------
// Section
// ---------------------------------------------------------------------------

interface SectionProps {
  title: string;
  /** Active filters inside. A section with any starts open, and opens when it gains its first. */
  active: number;
  defaultExpanded?: boolean;
  children: React.ReactNode;
}

const Section: React.FC<SectionProps> = ({ title, active, defaultExpanded = false, children }) => {
  const [expanded, setExpanded] = useState(defaultExpanded || active > 0);
  // A filter that arrives from outside the panel (a link, the back button)
  // opens its section so the selection is in view.
  const wasActive = useRef(active > 0);
  useEffect(() => {
    if (active > 0 && !wasActive.current) setExpanded(true);
    wasActive.current = active > 0;
  }, [active]);

  return (
    <Accordion
      disableGutters
      square
      elevation={0}
      expanded={expanded}
      onChange={(_, next) => setExpanded(next)}
      slotProps={{ transition: { unmountOnExit: true } }}
      sx={{ borderTop: 1, borderColor: 'divider', '&::before': { display: 'none' } }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon fontSize="small" />}
        sx={{ minHeight: 44, px: 2, '& .MuiAccordionSummary-content': { my: 1, alignItems: 'center', gap: 1 } }}
      >
        <Typography variant="subtitle2" component="span">
          {title}
        </Typography>
        {active > 0 && (
          <Chip size="small" color="primary" label={active} aria-label={`${active} selected`} sx={{ height: 18, fontSize: '0.7rem' }} />
        )}
      </AccordionSummary>
      <AccordionDetails sx={{ px: 2, pt: 0, pb: 1.5 }}>{children}</AccordionDetails>
    </Accordion>
  );
};

// ---------------------------------------------------------------------------
// Checkbox group with counts
// ---------------------------------------------------------------------------

interface CheckboxGroupProps {
  label: string;
  options: FilterOption[];
  /** Show this many, with the rest behind "show more". */
  limit?: number;
  /** Shown in place of the options when there are none. */
  emptyText: string;
  onToggle: (value: string) => void;
}

const CheckboxGroup: React.FC<CheckboxGroupProps> = ({ label, options, limit, emptyText, onToggle }) => {
  const [showAll, setShowAll] = useState(false);

  if (options.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        {emptyText}
      </Typography>
    );
  }

  // Selected options stay visible even when they sit past the fold.
  const visible = showAll || !limit ? options : options.filter((option, index) => index < limit || option.selected);
  const hidden = options.length - visible.length;

  return (
    <>
      <FormGroup role="group" aria-label={label}>
        {visible.map((option) => (
          <FormControlLabel
            key={option.value}
            // Nothing to gain from picking an option with no jobs, but one
            // that is already picked must stay removable.
            disabled={option.count === 0 && !option.selected}
            control={
              <Checkbox
                size="small"
                checked={option.selected}
                onChange={() => onToggle(option.value)}
                sx={{ p: 0.5, mr: 0.5 }}
              />
            }
            label={<OptionLabel label={option.label} count={option.count} />}
            sx={optionRowSx}
          />
        ))}
      </FormGroup>
      {limit !== undefined && options.length > limit && (hidden > 0 || showAll) && (
        <Button size="small" onClick={() => setShowAll((value) => !value)} aria-expanded={showAll} sx={{ mt: 0.5, ml: -0.5 }}>
          {showAll ? 'Show fewer' : `Show ${hidden} more`}
        </Button>
      )}
    </>
  );
};

// ---------------------------------------------------------------------------
// Coverage: jobs whose application questions are already answered
// ---------------------------------------------------------------------------

interface CoverageFilterProps {
  value: CoverageLevel | null;
  /** null until the counts have loaded. */
  counts: CoverageFacets | null;
  onChange: (value: CoverageLevel | null) => void;
}

const CoverageFilter: React.FC<CoverageFilterProps> = ({ value, counts, onChange }) => {
  // Nothing has been read yet: the buttons would only ever show zero.
  const noneKnown = counts !== null && counts.known === 0;
  return (
    <Box sx={{ px: 2, pb: 1.5 }}>
      <Typography variant="subtitle2" component="h3" sx={{ mb: 0.75 }}>
        Application questions
      </Typography>
      <ToggleButtonGroup
        exclusive
        fullWidth
        size="small"
        color="primary"
        value={value ?? 'all'}
        onChange={(_, next: CoverageLevel | 'all' | null) => {
          // Clicking the pressed button gives null; keep the choice instead.
          if (next === null) return;
          onChange(next === 'all' ? null : next);
        }}
        aria-label="Application questions"
        sx={{ '& .MuiToggleButton-root': { textTransform: 'none', lineHeight: 1.2, py: 0.75, px: 0.5, fontSize: '0.8rem' } }}
      >
        <ToggleButton value="all">All</ToggleButton>
        {COVERAGE_LEVELS.map((level) => {
          const count = counts ? counts[level] : null;
          return (
            <ToggleButton key={level} value={level} disabled={count === 0 && value !== level}>
              <Box component="span" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <span>{COVERAGE_LABELS[level]}</span>
                <Box component="span" sx={{ fontSize: '0.7rem', color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
                  {formatCount(count)}
                </Box>
              </Box>
            </ToggleButton>
          );
        })}
      </ToggleButtonGroup>
      <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.75, mb: 0 }}>
        {noneKnown ? (
          'No application forms have been read yet.'
        ) : (
          <>
            Jobs whose required questions your{' '}
            <Link component={RouterLink} to="/answers">
              Answer Library
            </Link>{' '}
            already covers, or all but two of them.
          </>
        )}
      </Typography>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Staffing agencies: hidden unless the switch is turned off
// ---------------------------------------------------------------------------

interface AgencyFilterProps {
  hidden: boolean;
  /** Agency listings among the matches; null until the counts have loaded. */
  count: number | null;
  /** Set apart from a block above it. */
  divided: boolean;
  onChange: (hidden: boolean) => void;
}

const AGENCY_CAPTION_ID = 'job-filter-agencies-caption';

const AgencyFilter: React.FC<AgencyFilterProps> = ({ hidden, count, divided, onChange }) => (
  <Box sx={{ px: 2, pb: 1.5, ...(divided && { pt: 1, borderTop: 1, borderColor: 'divider' }) }}>
    <FormControlLabel
      control={
        <Switch
          size="small"
          checked={hidden}
          onChange={(e) => onChange(e.target.checked)}
          inputProps={{ 'aria-describedby': AGENCY_CAPTION_ID }}
        />
      }
      label="Hide staffing agencies"
      labelPlacement="start"
      sx={{
        mx: 0,
        width: '100%',
        justifyContent: 'space-between',
        '& .MuiFormControlLabel-label': { fontSize: '0.875rem', fontWeight: 500 },
      }}
    />
    <Typography id={AGENCY_CAPTION_ID} variant="caption" color="text.secondary" component="p" sx={{ m: 0 }}>
      {agencyCaption(hidden, count)}
    </Typography>
  </Box>
);

// ---------------------------------------------------------------------------
// Panel
// ---------------------------------------------------------------------------

const JobFilterPanel: React.FC<Props> = ({ state, facets, facetsFailed, onRetryFacets, signedIn, staff = false, onChange }) => {
  const emptyText = facets
    ? 'No options for the current filters.'
    : facetsFailed
      ? 'Options could not be loaded.'
      : 'Loading options...';

  const options = (group: FacetGroup) => mergeFacetOptions(group, facetRows(facets, group), state[group]);

  const toggle = (group: FacetGroup) => (value: string) => {
    const current = state[group];
    onChange({ [group]: current.includes(value) ? current.filter((v) => v !== value) : [...current, value] });
  };

  const commitLocation = useCallback((value: string) => onChange({ location: value }, { replace: true }), [onChange]);
  const commitSalary = useCallback(
    (value: string) => onChange({ salary_min: value ? Number(value) : null }, { replace: true }),
    [onChange]
  );

  const postedValues = POSTED_WITHIN_OPTIONS.map((option) => option.value);
  // A value that came in through the URL and is not one of the presets.
  const customPosted = state.posted_within && !postedValues.includes(state.posted_within) ? state.posted_within : null;

  // Signed in, the API always counts coverage; an older API that does not is
  // the only reason to hide the control once the counts arrive.
  const showCoverage = signedIn && Boolean(state.coverage || !facets || facets.coverage);

  return (
    <Box>
      {facetsFailed && (
        <Alert
          severity="warning"
          sx={{ mx: 2, mb: 1.5 }}
          action={
            <Button color="inherit" size="small" onClick={onRetryFacets}>
              Retry
            </Button>
          }
        >
          Could not load the counts.
        </Alert>
      )}

      {showCoverage && (
        <CoverageFilter
          value={state.coverage}
          counts={facets?.coverage ?? null}
          onChange={(coverage) => onChange({ coverage })}
        />
      )}

      <AgencyFilter
        hidden={state.hide_agencies}
        count={facets ? facets.staffing_agency_jobs ?? null : null}
        divided={showCoverage}
        onChange={(hide_agencies) => onChange({ hide_agencies })}
      />

      {signedIn && (
        <Section title="My jobs" active={countSelected(state, ['hide_applied', 'saved'])} defaultExpanded>
          <FormGroup role="group" aria-label="My jobs">
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={state.hide_applied}
                  onChange={(e) => onChange({ hide_applied: e.target.checked })}
                  sx={{ p: 0.5, mr: 0.5 }}
                />
              }
              label="Hide jobs I applied to"
              sx={optionRowSx}
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={state.saved}
                  onChange={(e) => onChange({ saved: e.target.checked })}
                  sx={{ p: 0.5, mr: 0.5 }}
                />
              }
              label="Saved only"
              sx={optionRowSx}
            />
          </FormGroup>
        </Section>
      )}

      <Section title="Workplace" active={state.workplace.length} defaultExpanded>
        <CheckboxGroup label="Workplace" options={options('workplace')} emptyText={emptyText} onToggle={toggle('workplace')} />
      </Section>

      <Section title="Level" active={state.level.length} defaultExpanded>
        <CheckboxGroup label="Level" options={options('level')} emptyText={emptyText} onToggle={toggle('level')} />
      </Section>

      <Section title="Category" active={state.category.length} defaultExpanded>
        <CheckboxGroup
          label="Category"
          options={options('category')}
          limit={8}
          emptyText={emptyText}
          onToggle={toggle('category')}
        />
      </Section>

      <Section title="Job type" active={state.job_type.length}>
        <CheckboxGroup label="Job type" options={options('job_type')} emptyText={emptyText} onToggle={toggle('job_type')} />
      </Section>

      <Section title="Location" active={countSelected(state, ['location', 'country'])} defaultExpanded>
        <DebouncedTextField
          value={state.location}
          onCommit={commitLocation}
          normalize={normalizeText}
          label="City, state or region"
          size="small"
          fullWidth
          clearLabel="Clear location"
          sx={{ mt: 0.5, mb: 1.5 }}
        />
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
          Country
        </Typography>
        <CheckboxGroup
          label="Country"
          options={options('country')}
          limit={6}
          emptyText={emptyText}
          onToggle={toggle('country')}
        />
      </Section>

      <Section title="Posted" active={state.posted_within ? 1 : 0} defaultExpanded>
        <RadioGroup
          aria-label="Posted within"
          value={state.posted_within ? String(state.posted_within) : ''}
          onChange={(e) => onChange({ posted_within: e.target.value ? Number(e.target.value) : null })}
        >
          <FormControlLabel
            value=""
            control={<Radio size="small" sx={{ p: 0.5, mr: 0.5 }} />}
            label="Any time"
            sx={optionRowSx}
          />
          {POSTED_WITHIN_OPTIONS.map((option) => (
            <FormControlLabel
              key={option.value}
              value={String(option.value)}
              control={<Radio size="small" sx={{ p: 0.5, mr: 0.5 }} />}
              label={option.label}
              sx={optionRowSx}
            />
          ))}
          {customPosted && (
            <FormControlLabel
              value={String(customPosted)}
              control={<Radio size="small" sx={{ p: 0.5, mr: 0.5 }} />}
              label={postedWithinLabel(customPosted)}
              sx={optionRowSx}
            />
          )}
        </RadioGroup>
      </Section>

      <Section title="Salary" active={countSelected(state, ['salary_min', 'has_salary'])} defaultExpanded>
        <Stack spacing={1}>
          <DebouncedTextField
            value={state.salary_min ? String(state.salary_min) : ''}
            onCommit={commitSalary}
            normalize={normalizeAmount}
            label="Minimum salary, per year"
            placeholder="120000"
            size="small"
            fullWidth
            inputProps={{ inputMode: 'numeric' }}
            clearLabel="Clear minimum salary"
            helperText="Jobs whose stated range reaches this amount."
            sx={{ mt: 0.5 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={state.has_salary}
                onChange={(e) => onChange({ has_salary: e.target.checked })}
                sx={{ p: 0.5, mr: 0.5 }}
              />
            }
            label={<OptionLabel label="Only jobs with a stated salary" count={facets ? facets.with_salary ?? null : null} />}
            sx={optionRowSx}
          />
        </Stack>
      </Section>

      <Section title="Technologies" active={state.tech.length}>
        <CheckboxGroup
          label="Technologies"
          options={options('tech')}
          limit={10}
          emptyText={emptyText}
          onToggle={toggle('tech')}
        />
      </Section>

      <Section title="Company" active={state.company.length}>
        <CheckboxGroup
          label="Company"
          options={options('company')}
          limit={10}
          emptyText={emptyText}
          onToggle={toggle('company')}
        />
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1, mb: 0 }}>
          The companies with the most matching jobs. Use the search box to find another by name.
        </Typography>
      </Section>

      {/* Which hiring system a job came from: for staff only. */}
      {staff && (
        <Section title="ATS (staff)" active={state.ats.length}>
          <CheckboxGroup label="ATS" options={options('ats')} emptyText={emptyText} onToggle={toggle('ats')} />
        </Section>
      )}
    </Box>
  );
};

export default JobFilterPanel;
